/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  GetCandleDataOptions,
  GetCandleDataStatus200,
  GetCandleDataStatus400,
  GetCandleDataStatus404,
  GetCandleDataStatus500,
} from '../types/GetCandleData'
import type {
  QueryKey,
  QueryClient,
  QueryObserverOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { getCandleData } from '../clients/getCandleData'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getCandleDataQueryKey = ({ path, query }: Omit<GetCandleDataOptions, 'headers'>) =>
  [{ url: '/v1/charts/:order_book_id/candle', params: path }, ...(query ? [query] : [])] as const

type GetCandleDataQueryKey = ReturnType<typeof getCandleDataQueryKey>

export function getCandleDataQueryOptions(
  { path, query }: GetCandleDataOptions,
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const queryKey = getCandleDataQueryKey({ path, query })
  return queryOptions<
    GetCandleDataStatus200,
    ResponseErrorConfig<GetCandleDataStatus400 | GetCandleDataStatus404 | GetCandleDataStatus500>,
    GetCandleDataStatus200,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      return getCandleData({
        ...config,
        path,
        query,
        signal: config.signal ?? signal,
        throwOnError: true,
      }).unwrap()
    },
  })
}

/**
 * @description Returns candle data in the requested [start, end) range for the selected resolution, capped to the most recent 5,000 candles per request. Public callers may query data from up to the last month, while authenticated callers may query up to the last six months (requests with invalid credentials will be rejected as unauthorized).
 * @summary Get candlestick data for an orderbook
 * {@link /v1/charts/:order_book_id/candle}
 */
export function useGetCandleData<
  TData = GetCandleDataStatus200,
  TQueryData = GetCandleDataStatus200,
  TQueryKey extends QueryKey = GetCandleDataQueryKey,
>(
  {
    path,
    query,
  }: {
    path: GetCandleDataOptions['path'] | (() => GetCandleDataOptions['path'])
    query: GetCandleDataOptions['query'] | (() => GetCandleDataOptions['query'])
  },
  options: {
    query?: Partial<
      QueryObserverOptions<
        GetCandleDataStatus200,
        ResponseErrorConfig<
          GetCandleDataStatus400 | GetCandleDataStatus404 | GetCandleDataStatus500
        >,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const resolvedParams = {
    path: typeof path === 'function' ? path() : path,
    query: typeof query === 'function' ? query() : query,
  }
  const queryKey = resolvedOptions?.queryKey ?? getCandleDataQueryKey(resolvedParams)

  const queryResult = useQuery(
    {
      ...getCandleDataQueryOptions(resolvedParams, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as QueryObserverOptions,
    queryClient,
  ) as UseQueryResult<
    TData,
    ResponseErrorConfig<GetCandleDataStatus400 | GetCandleDataStatus404 | GetCandleDataStatus500>
  > & { queryKey: TQueryKey }

  queryResult.queryKey = queryKey as TQueryKey

  return queryResult
}
